/**
 * A Style Lib for atypicals.
 *
 * @description For highly-customized and fixed overrides that are yet to be considered as
 * sensible options to insert in the db the app to await in a fetch from CustomerOptionsApi (Composer).
 *
 * Note these are header-specific only as forthcoming reskins all require a subheader redesign.
 * The purpose is to be able to extend default styles, and retrieve custom styles via `versionKey`.
 *
 * @todo Coordinate with `@devicebits/component-library` for all things between headers and footers.
 * @todo Refactor into `GlobalStyleInject` with merged custOptions in styleProvider.
 * @see {@link module: src/constants/index.js CUSTOM_CUSTOM_GLOBAL_CUSTOMER_VERSION_KEYS}
 * @see {@link module:'../constants/index.js' CUSTOM_HEADER_AND_FOOTER_CUSTOMERS}
 * @see getPalette
 *
 * This expands on the `styled-system` spec used by styled-components theme interface.
 * @link https://styled-system.com/
 * @link https://styled-system.com/theme-specification
 */

/**
 * Sensible Constants that reflect majority of overrides in reskinned headers
 * for v2, v3 and forthcoming v4. To be used as a reference and to be refactored to further
 * systematize once rehearsed and reviewed with team.
 */

import { getPalette } from '../helpers/utils'

export const V2_SUBHEADER_BACKGROUND_COLOR = '#231f20'
export const V2_SUBHEADER_HEIGHT_MD = '3.75rem'
export const V2_SUBHEADER_HEIGHT_SM = 'auto'

export const V2_SUBNAV_HEIGHT_MD = '3.75rem'
export const V2_SUBNAV_HEIGHT_SM = '3.75rem'

export const V2_SUBNAV_MARGIN_LEFT_LG = '40px'
export const V2_SUBNAV_MARGIN_LEFT_MD = '0px'
export const V2_SUBNAV_MARGIN_LEFT_SM = '0px'

export const V2_SUBNAV_CONTAINER_PADDING = '0 8px'
export const V2_SUBNAV_CONTAINER_WIDTH = '100%'

export const V2_TAB_BORDER_RADIUS = 0
export const V2_TAB_BORDER_BOTTOM = '1px solid #e8e8e9'

export const V2_TAB_FONT_SIZE_MD = '12px'
export const V2_TAB_FONT_SIZE_SM = '14px'
export const V2_TAB_FONT_WEIGHT = '500'
export const V2_TAB_LETTER_SPACING = '.25px'
export const V2_TAB_TEXT_TRANSFORM = 'capitalize'
export const V2_TAB_LINE_HEIGHT = 'none'
export const V2_TAB_PADDING_BOTTOM = '20px'

export const V2_TAB_KEYLINE_TOP_LG = '100%'
export const V2_TAB_KEYLINE_TOP_MD = '92%'
export const V2_TAB_KEYLINE_TOP_SM = '90%'
export const V2_TAB_KEYLINE_HEIGHT_MD = '6px'
export const V2_TAB_KEYLINE_HEIGHT_SM = '4px'
export const V2_TAB_KEYLINE_WIDTH = '100%'
export const V2_TAB_KEYLINE_BACKGROUND_COLOR = '#bee81e'
export const V2_TAB_KEYLINE_BORDER_RADIUS_MD = '10px'
export const V2_TAB_KEYLINE_BORDER_RADIUS_SM = '10px 10px 0 0'

export const V2_TAB_SVG_SIZE_MD = '20px'
export const V2_TAB_SVG_SIZE_SM = '22px'

/**
 * V2-specific SubHeader styles
 */
/** SubHeader Container */
export const V2_SUBHEADER_STYLES = {
  marginTop: {
    md: V2_SUBHEADER_HEIGHT_MD,
    sm: V2_SUBHEADER_HEIGHT_SM
  },
  backgroundColor: {
    md: V2_SUBHEADER_BACKGROUND_COLOR,
    sm: V2_SUBHEADER_BACKGROUND_COLOR
  },
  height: {
    md: V2_SUBNAV_HEIGHT_MD,
    sm: V2_SUBNAV_HEIGHT_SM
  },
  marginLeft: {
    lg: V2_SUBNAV_MARGIN_LEFT_LG,
    md: V2_SUBNAV_MARGIN_LEFT_MD,
    sm: V2_SUBNAV_MARGIN_LEFT_SM
  },
  width: {
    md: V2_SUBNAV_CONTAINER_WIDTH,
    sm: V2_SUBNAV_CONTAINER_WIDTH
  },
  padding: {
    md: V2_SUBNAV_CONTAINER_PADDING,
    sm: V2_SUBNAV_CONTAINER_PADDING
  }
}

/** SubHeader Tabs */
export const V2_TAB_STYLES = {
  fontSize: {
    md: V2_TAB_FONT_SIZE_MD,
    sm: V2_TAB_FONT_SIZE_SM
  },
  fontWeight: {
    md: V2_TAB_FONT_WEIGHT,
    sm: V2_TAB_FONT_WEIGHT
  },
  letterSpacing: {
    md: V2_TAB_LETTER_SPACING,
    sm: V2_TAB_LETTER_SPACING
  },
  textTransform: {
    md: V2_TAB_TEXT_TRANSFORM,
    sm: V2_TAB_TEXT_TRANSFORM
  },
  borderRadius: {
    md: V2_TAB_BORDER_RADIUS,
    sm: V2_TAB_BORDER_RADIUS
  },
  borderBottom: {
    md: V2_TAB_BORDER_BOTTOM,
    sm: V2_TAB_BORDER_BOTTOM
  },
  keylineTop: {
    lg: V2_TAB_KEYLINE_TOP_LG,
    md: V2_TAB_KEYLINE_TOP_MD,
    sm: V2_TAB_KEYLINE_TOP_SM
  },
  keylineBorderRadius: {
    md: V2_TAB_KEYLINE_BORDER_RADIUS_MD,
    sm: V2_TAB_KEYLINE_BORDER_RADIUS_SM
  },
  keylineHeight: {
    lg: V2_TAB_KEYLINE_HEIGHT_SM,
    md: V2_TAB_KEYLINE_HEIGHT_MD,
    sm: V2_TAB_KEYLINE_HEIGHT_SM
  },
  keylineWidth: {
    md: V2_TAB_KEYLINE_WIDTH,
    sm: V2_TAB_KEYLINE_WIDTH
  },
  keylineBackgroundColor: {
    md: V2_TAB_KEYLINE_BACKGROUND_COLOR,
    sm: V2_TAB_KEYLINE_BACKGROUND_COLOR
  },
  svgSize: {
    md: V2_TAB_SVG_SIZE_MD,
    sm: V2_TAB_SVG_SIZE_SM
  }
}

/**
 * V3-specific SubHeader styles
 */
/** SubHeader Container */
export const V3_SUBHEADER_STYLES = {
  marginTop: {
    md: '3.75rem',
    sm: 'auto'
  },
  backgroundColor: {
    md: '#000f9f',
    sm: '#000f9f'
  },
  height: {
    md: '3.75rem',
    sm: '3.75rem'
  },
  marginLeft: {
    lg: '40px',
    md: '0px',
    sm: '0px'
  },
  /** There are duplicates to accommodate versions where breakpoints are at different values */
  width: {
    md: '100%',
    sm: '100%'
  },
  padding: {
    md: '0 8px',
    sm: '0 8px'
  }
}

/** SubHeader Tabs */
export const V3_TAB_STYLES = {
  fontSize: {
    md: '12px',
    sm: '14px'
  },
  fontWeight: {
    md: '500',
    sm: '500'
  },
  letterSpacing: {
    md: '.25px',
    sm: '.25px'
  },
  textTransform: {
    md: 'capitalize',
    sm: 'capitalize'
  },
  borderRadius: {
    md: 0,
    sm: 0
  },
  borderBottom: {
    md: '1px solid #e8e8e9',
    sm: '1px solid #e8e8e9'
  },
  keylineTop: {
    lg: '100%',
    md: '92%',
    sm: '90%'
  },
  keylineBorderRadius: {
    md: '10px',
    sm: '10px 10px 0 0'
  },
  keylineHeight: {
    lg: '4px',
    md: '6px',
    sm: '4px'
  },
  keylineWidth: {
    md: '100%',
    sm: '100%'
  },
  keylineBackgroundColor: {
    md: '#bee81e',
    sm: '#bee81e'
  },
  svgSize: {
    md: '20px',
    sm: '22px'
  }
}

/**
 * V4-specific SubHeader styles
 * @todo dupe of v3 as placeholder
 */
/** SubHeader Container */
export const V4_SUBHEADER_STYLES = {
  marginTop: {
    md: '3rem',
    sm: 'auto'
  },
  backgroundColor: {
    md: '#ee0000',
    sm: '#ee0000'
  },
  height: {
    md: '3.75rem',
    sm: '3.75rem'
  },
  marginLeft: {
    lg: '40px',
    md: '0px',
    sm: '0px'
  },

  /** There are duplicates to accommodate versions where breakpoints are at different values */
  width: {
    md: '100%',
    sm: '100%'
  },
  padding: {
    md: '0 8px',
    sm: '0 8px'
  }
}

/** SubHeader Tabs @todo dupe of v3 as placeholder */
export const V4_TAB_STYLES = {
  fontSize: {
    md: '12px',
    sm: '14px'
  },
  fontWeight: {
    md: '500',
    sm: '500'
  },
  letterSpacing: {
    md: '.25px',
    sm: '.25px'
  },
  textTransform: {
    md: 'capitalize',
    sm: 'capitalize'
  },
  borderRadius: {
    md: 0,
    sm: 0
  },
  borderBottom: {
    md: '1px solid #e8e8e9',
    sm: '1px solid #e8e8e9'
  },
  keylineTop: {
    lg: '100%',
    md: '92%',
    sm: '90%'
  },
  keylineBorderRadius: {
    md: '0px',
    sm: '0px'
  },
  keylineHeight: {
    lg: '4px',
    md: '6px',
    sm: '4px'
  },
  keylineWidth: {
    md: '100%',
    sm: '40%'
  },
  keylineBackgroundColor: {
    md: '#fff',
    sm: '#fff'
  },
  svgSize: {
    md: '30px',
    sm: '30px'
  }
}

/**
 * Fallbacks to return to defaults
 * Note: This has no consequence to non-`CUSTOM_HEADER_AND_FOOTER_CUSTOMERS` components
 * as these already live within a compontent tree of a verioned  override.
 */
/** SubHeader Container */
export const V1_SUBHEADER_STYLES = {
  marginTop: {
    md: 'auto',
    sm: 'auto'
  },
  backgroundColor: {
    md: 'var(--clr-main-to-black)',
    sm: 'var(--clr-main-to-black)'
  },
  height: {
    md: '3.75rem',
    sm: '3.75rem'
  },
  marginLeft: {
    lg: 'unset',
    md: 'unset',
    sm: 0
  },
  width: {
    md: '',
    sm: ''
  },
  padding: {
    md: 'inherit',
    sm: 0
  }
}

/**
 * Default styles retrievable by version key.
 * @todo organize directory
 * @todo set up `v4` for new reskin customer before 5/5/22
 */
const STYLES = {
  v1: {
    subheader: V1_SUBHEADER_STYLES
  },
  v2: {
    subheader: V2_SUBHEADER_STYLES,
    tabs: V2_TAB_STYLES
  },
  v3: {
    subheader: V3_SUBHEADER_STYLES,
    tabs: V3_TAB_STYLES
  },
  /**
   * Dupe of v3 as placeholder for v4
   * @todo update to v4 mocks
   */
  v4: {
    subheader: V4_SUBHEADER_STYLES,
    tabs: V4_TAB_STYLES
  }
}

export const getPalettePath = getPalette(STYLES)

export default STYLES



// WEBPACK FOOTER //
// ./src/styles/constants.js